import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Timer, Upload, RefreshCw, Trash2, FileText, Building2,
  Clock, Car, AlertTriangle, TrendingUp, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TimeBar({ label, value, max, color = 'bg-blue-500' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const isWarning = value > 120;
  const isCritical = value > 180;
  const barColor = isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : color;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-600">{label}</span>
        <span className={`font-semibold ${isCritical ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-900'}`}>{value}s</span>
      </div>
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function ServiceTimes() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState('all');
  const fileRef = useRef(null);

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/reports/service-times`);
      setReports(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/reports/upload-service-times`, formData, {
        timeout: 120000,
      });
      toast.success(res.data.message);
      await fetchReports();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error(detail || 'Failed to parse PDF. Please try again.');
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (reportId) => {
    try {
      await axios.delete(`${API_URL}/reports/${reportId}`);
      toast.success('Report deleted');
      fetchReports();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const filteredReports = selectedReportId === 'all' ? reports : reports.filter(r => r.id === selectedReportId);

  // Aggregate for comparison when showing all
  const storeComparison = reports.reduce((acc, r) => {
    const name = r.report_info?.store_name || 'Unknown';
    if (!acc[name]) acc[name] = [];
    acc[name].push(r);
    return acc;
  }, {});

  if (!['owner', 'area_manager', 'manager'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <Timer className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h2 className="text-lg font-bold text-slate-900 mb-2">Access Restricted</h2>
        <p className="text-slate-600">Only authorized users can view service times</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-times-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Timer className="w-8 h-8" />
              Drive-Thru Service Times
            </h1>
            <p className="text-white/80 text-sm mt-1">Speed of service analysis & store comparisons</p>
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".pdf" onChange={handleUpload} className="hidden" />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-white text-orange-600 hover:bg-orange-50 rounded-sm font-semibold"
              data-testid="upload-service-times-btn"
            >
              {uploading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Parsing PDF...</> : <><Upload className="w-4 h-4 mr-2" /> Upload Service Report</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Report filter */}
      {reports.length > 0 && (
        <div className="flex flex-wrap items-center gap-4">
          <Select value={selectedReportId} onValueChange={setSelectedReportId}>
            <SelectTrigger className="w-72 rounded-sm border-slate-200" data-testid="service-report-selector">
              <Building2 className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="All stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores (Comparison)</SelectItem>
              {reports.map(r => (
                <SelectItem key={r.id} value={r.id}>
                  {r.report_info?.store_name} - {r.report_info?.week_ending}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* No Reports */}
      {!loading && reports.length === 0 && (
        <Card className="bg-white border border-slate-200">
          <CardContent className="py-12 text-center">
            <Timer className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Service Time Reports Yet</h3>
            <p className="text-slate-500 mb-4">Upload a Drive-Thru Periods or POS Speed of Service PDF</p>
            <Button onClick={() => fileRef.current?.click()} className="bg-teal-600 hover:bg-teal-700 text-white">
              <Upload className="w-4 h-4 mr-2" /> Upload PDF
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Store Comparison View */}
      {selectedReportId === 'all' && Object.keys(storeComparison).length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader className="pb-2 border-b border-slate-100">
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <BarChart3 className="w-5 h-5 text-teal-600" />
              Store-by-Store Service Time Comparison
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="service-comparison-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Store</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Week</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Total Cars</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Avg Service (s)</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Avg Queue (s)</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Drive Offs</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r, i) => {
                    const s = r.summary || {};
                    const isPoS = r.report_info?.report_type === 'pos_speed_of_service';
                    // For POS reports, calculate avg ticket time from periods; for DT reports use service time
                    let avgService = s.overall_avg_service_time || 0;
                    if (isPoS && avgService === 0) {
                      const periods = r.time_periods || [];
                      const ticketTimes = periods.filter(p => p.overall_avg_ticket_time > 0);
                      if (ticketTimes.length > 0) {
                        avgService = Math.round(ticketTimes.reduce((sum, p) => sum + p.overall_avg_ticket_time, 0) / ticketTimes.length);
                      }
                    }
                    return (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-3 px-4 font-medium text-slate-900">
                          {r.report_info?.store_name}
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{isPoS ? 'POS' : 'DT Timer'}</span>
                        </td>
                        <td className="py-3 px-4 text-right text-slate-600">{r.report_info?.week_ending}</td>
                        <td className="py-3 px-4 text-right">{s.total_cars?.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${avgService <= 80 ? 'bg-green-50 text-green-600' : avgService <= 120 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                            {avgService}s
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">{s.overall_avg_queue_time || 0}s</td>
                        <td className="py-3 px-4 text-right">
                          <span className={`${(s.total_drive_offs || 0) > 5 ? 'text-red-500' : 'text-slate-600'}`}>{s.total_drive_offs || 0}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          {['owner', 'area_manager'].includes(user?.role) && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="text-red-400 hover:text-red-600 h-7 px-2">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Individual Report Detail */}
      {filteredReports.map((report) => {
        const isPoS = report.report_info?.report_type === 'pos_speed_of_service';
        let displayAvgService = report.summary?.overall_avg_service_time || 0;
        let displayAvgQueue = report.summary?.overall_avg_queue_time || 0;
        if (isPoS && displayAvgService === 0) {
          const periods = report.time_periods || [];
          const ticketTimes = periods.filter(p => p.overall_avg_ticket_time > 0);
          if (ticketTimes.length > 0) {
            displayAvgService = Math.round(ticketTimes.reduce((sum, p) => sum + p.overall_avg_ticket_time, 0) / ticketTimes.length);
          }
        }
        return (
        <Card key={report.id} className="bg-white border border-slate-200 rounded-sm">
          <CardHeader className="pb-2 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <Building2 className="w-5 h-5 text-teal-600" />
                {report.report_info?.store_name} - Week of {report.report_info?.week_ending}
              </CardTitle>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{report.report_info?.report_type?.replace(/_/g, ' ')}</span>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-teal-50 rounded-lg p-3 text-center">
                <Car className="w-5 h-5 mx-auto text-teal-600 mb-1" />
                <p className="text-lg font-bold text-slate-900">{report.summary?.total_cars?.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Total Cars</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <Clock className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                <p className="text-lg font-bold text-slate-900">{displayAvgService}s</p>
                <p className="text-xs text-slate-500">{isPoS ? 'Avg Ticket Time' : 'Avg Service Time'}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <Timer className="w-5 h-5 mx-auto text-amber-600 mb-1" />
                <p className="text-lg font-bold text-slate-900">{displayAvgQueue}s</p>
                <p className="text-xs text-slate-500">Avg Queue Time</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <AlertTriangle className="w-5 h-5 mx-auto text-red-500 mb-1" />
                <p className="text-lg font-bold text-slate-900">{report.summary?.total_drive_offs}</p>
                <p className="text-xs text-slate-500">Drive Offs</p>
              </div>
            </div>

            {/* Time Period Breakdown */}
            <h4 className="text-sm font-semibold text-slate-700 mb-3">Service Times by Period</h4>
            <div className="space-y-4">
              {(report.time_periods || []).filter(p => p.period_name !== 'All Day' && p.period_name !== 'Regular Hours').map((period, i) => {
                const maxTime = 300;
                return (
                  <div key={i} className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-semibold text-sm text-slate-900">{period.period_name}</p>
                        <p className="text-xs text-slate-500">{period.start_time} - {period.end_time}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Cars: <strong>{period.car_count}</strong></p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {period.menu_board_avg_time > 0 && (
                        <TimeBar label="Menu Board" value={period.menu_board_avg_time} max={maxTime} color="bg-blue-400" />
                      )}
                      {period.greet_avg_time > 0 && (
                        <TimeBar label="Greet" value={period.greet_avg_time} max={maxTime} color="bg-green-400" />
                      )}
                      {period.service_avg_time > 0 && (
                        <TimeBar label="Service Window" value={period.service_avg_time} max={maxTime} color="bg-teal-500" />
                      )}
                      {period.overall_avg_ticket_time > 0 && (
                        <TimeBar label="Avg Ticket Time" value={period.overall_avg_ticket_time} max={700} color="bg-indigo-500" />
                      )}
                      {period.avg_queue_time > 0 && (
                        <TimeBar label="Queue Time" value={period.avg_queue_time} max={maxTime} color="bg-amber-400" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
}
